import { UserScenario, TaxResult, LocationWithCoordinates } from '@/types/taxExplorer';
import { getTaxRulesForCountry, taxRulesOverrides } from '@/data/taxRulesOverrides';
import { countries } from '@/data/countries';

// Calculate income tax based on brackets
function calculateProgressiveTax(income: number, brackets: { threshold: number; rate: number }[]): number {
  let tax = 0;
  let previousThreshold = 0;
  
  for (const bracket of brackets) {
    if (income <= previousThreshold) break;
    
    const taxableInBracket = Math.min(income, bracket.threshold) - previousThreshold;
    if (taxableInBracket > 0) {
      tax += taxableInBracket * (bracket.rate / 100);
    }
    previousThreshold = bracket.threshold;
  }
  
  return tax;
}

// Calculate aspiration match score (0-100)
function calculateAspirationScore(
  scenario: UserScenario,
  lifestyle: { 
    climate: string; 
    englishSpeaking: boolean; 
    euAccess: boolean; 
    cryptoFriendly: boolean;
    digitalNomadVisa: boolean;
    qualityOfLife: number;
  }
): number {
  let score = 50; // Base score
  
  // Lifestyle preferences matching
  if (scenario.lifestylePrefs.includes('warm-climate') && lifestyle.climate === 'warm') score += 15;
  if (scenario.lifestylePrefs.includes('english-speaking') && lifestyle.englishSpeaking) score += 10;
  if (scenario.lifestylePrefs.includes('eu-access') && lifestyle.euAccess) score += 15;
  if (scenario.lifestylePrefs.includes('crypto-friendly') && lifestyle.cryptoFriendly) score += 10;
  if (scenario.lifestylePrefs.includes('digital-nomad') && lifestyle.digitalNomadVisa) score += 10;
  
  // Quality of life adjustment
  score += (lifestyle.qualityOfLife - 5) * 2;
  
  // Residency type matching
  if (scenario.residencyType === 'digital-nomad' && lifestyle.digitalNomadVisa) score += 10;
  
  return Math.min(100, Math.max(0, score));
}

// Get color based on savings percentage
export function getColorForSavings(savingsPercent: number): string {
  if (savingsPercent >= 50) return 'hsl(142, 76%, 36%)'; // Green
  if (savingsPercent >= 25) return 'hsl(142, 76%, 46%)'; // Light green
  if (savingsPercent >= 10) return 'hsl(48, 96%, 53%)';  // Yellow
  if (savingsPercent >= 0) return 'hsl(25, 95%, 53%)';   // Orange
  return 'hsl(0, 84%, 60%)'; // Red - worse than current
}

// Calculate tax for a specific location
/**
 * How the stated income is earned changes which taxes apply:
 * investments are taxed as capital gains + dividends, diversified splits it.
 */
export function applyIncomeType(scenario: UserScenario): UserScenario {
  const g = scenario.grossIncome;
  if (scenario.incomeType === 'investments') {
    return { ...scenario, grossIncome: 0, capitalGains: scenario.capitalGains + g * 0.5, dividends: scenario.dividends + g * 0.5 };
  }
  if (scenario.incomeType === 'mixed') {
    return { ...scenario, grossIncome: g * 0.6, capitalGains: scenario.capitalGains + g * 0.2, dividends: scenario.dividends + g * 0.2 };
  }
  return scenario;
}

/** Colour by effective rate when there is no residence to compare against. */
export function getColorForEffectiveRate(rate: number): string {
  if (rate <= 10) return 'hsl(142, 76%, 36%)';
  if (rate <= 20) return 'hsl(142, 76%, 46%)';
  if (rate <= 30) return 'hsl(48, 96%, 53%)';
  if (rate <= 40) return 'hsl(25, 95%, 53%)';
  return 'hsl(0, 84%, 60%)';
}

export function calculateForLocation(
  rawScenario: UserScenario,
  targetLocation: LocationWithCoordinates,
  currentTax: number
): TaxResult {
  const scenario = applyIncomeType(rawScenario);
  const isoCode = targetLocation.iso_alpha2;
  const taxRules = getTaxRulesForCountry(isoCode, getBasicTaxRate(isoCode));
  
  // Calculate income tax
  let incomeTax = 0;
  if (taxRules.personalIncomeTax.type === 'none') {
    incomeTax = 0;
  } else if (taxRules.personalIncomeTax.type === 'flat') {
    incomeTax = scenario.grossIncome * (taxRules.personalIncomeTax.topRate / 100);
  } else if (taxRules.personalIncomeTax.brackets) {
    incomeTax = calculateProgressiveTax(scenario.grossIncome, taxRules.personalIncomeTax.brackets);
  } else {
    // Estimate based on top rate
    incomeTax = scenario.grossIncome * (taxRules.personalIncomeTax.topRate / 100) * 0.7;
  }
  
  // Calculate other taxes
  const capitalGainsTax = scenario.capitalGains * (taxRules.capitalGainsTax.rate / 100);
  const dividendTax = scenario.dividends * (taxRules.dividendTax.rate / 100);
  const wealthTax = taxRules.wealthTax.hasWealthTax && scenario.netWorth > (taxRules.wealthTax.threshold || 0)
    ? (scenario.netWorth - (taxRules.wealthTax.threshold || 0)) * ((taxRules.wealthTax.rate || 0) / 100)
    : 0;
  
  const totalTax = incomeTax + capitalGainsTax + dividendTax + wealthTax;
  const totalIncome = scenario.grossIncome + scenario.capitalGains + scenario.dividends;
  const effectiveRate = totalIncome > 0 ? (totalTax / totalIncome) * 100 : 0;
  
  const savingsVsCurrent = currentTax - totalTax;
  const savingsPercent = currentTax > 0 ? (savingsVsCurrent / currentTax) * 100 : 0;
  
  // Check residency requirement
  const residencyMet = scenario.plannedDays >= taxRules.residencyRule.daysRequired;
  
  // Calculate aspiration score
  const aspirationScore = calculateAspirationScore(scenario, taxRules.lifestyle);
  
  // Generate highlights
  const highlights: string[] = [];
  if (taxRules.personalIncomeTax.type === 'none') highlights.push('0% income tax');
  if (taxRules.capitalGainsTax.rate === 0) highlights.push('0% capital gains');
  if (!taxRules.wealthTax.hasWealthTax) highlights.push('No wealth tax');
  if (taxRules.personalIncomeTax.specialPrograms?.length) {
    highlights.push(...taxRules.personalIncomeTax.specialPrograms);
  }
  if (taxRules.lifestyle.euAccess) highlights.push('EU access');
  if (taxRules.lifestyle.digitalNomadVisa) highlights.push('Digital nomad visa');
  
  return {
    locationId: targetLocation.location_id,
    countryCode: isoCode || '',
    countryName: targetLocation.name,
    effectiveRate: Math.round(effectiveRate * 10) / 10,
    totalTax: Math.round(totalTax),
    savingsVsCurrent: Math.round(savingsVsCurrent),
    savingsPercent: Math.round(savingsPercent),
    breakdown: {
      incomeTax: Math.round(incomeTax),
      capitalGainsTax: Math.round(capitalGainsTax),
      dividendTax: Math.round(dividendTax),
      wealthTax: Math.round(wealthTax)
    },
    residencyMet,
    aspirationScore: Math.round(aspirationScore),
    color: currentTax > 0 ? getColorForSavings(savingsPercent) : getColorForEffectiveRate(effectiveRate),
    highlights
  };
}

// Get basic tax rate from countries.ts
function getBasicTaxRate(isoCode: string | null): number {
  if (!isoCode) return 25;
  const country = countries.find(c => c.code === isoCode);
  return country?.taxRate || 25;
}

// Calculate current tax burden
export function calculateCurrentTax(scenario: UserScenario, locations: LocationWithCoordinates[]): number {
  const currentLocation = locations.find(l => l.location_id === scenario.currentResidencyId);
  if (!currentLocation) return 0;
  
  const dummyResult = calculateForLocation(
    { ...scenario, plannedDays: 365 },
    currentLocation,
    0
  );
  
  return dummyResult.totalTax;
}

// Calculate all results for all locations
export function calculateAllLocations(
  scenario: UserScenario,
  locations: LocationWithCoordinates[]
): TaxResult[] {
  // First calculate current tax
  const currentTax = calculateCurrentTax(scenario, locations);
  
  // Then calculate for all locations
  return locations
    .filter(l => l.iso_alpha2) // Only countries with ISO codes
    .map(location => calculateForLocation(scenario, location, currentTax));
}

/** Top-N places for this scenario: lowest tax first, lifestyle match breaks ties. */
export function rankBestForYou(results: TaxResult[], n = 5, excludeId: number | null = null): TaxResult[] {
  return results
    .filter(r => r.locationId !== excludeId)
    .slice()
    .sort((a, b) => a.totalTax - b.totalTax || b.aspirationScore - a.aspirationScore)
    .slice(0, n);
}

// Get priority jurisdictions (those with detailed rules)
export function getPriorityJurisdictions(): string[] {
  return Object.keys(taxRulesOverrides);
}
